import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
});

// Helper para formatear errores de validación
export const formatErrorMessage = (error) => {
  if (!error?.response?.data) return error?.message || 'Error desconocido';
  
  const detail = error.response.data.detail;
  
  // Si es un string, retornarlo directamente
  if (typeof detail === 'string') return detail;
  
  // Si es un array de errores de validación de Pydantic
  if (Array.isArray(detail)) {
    return detail.map(err => {
      const field = err.loc?.join('.') || 'campo';
      const msg = err.msg || 'error de validación';
      return `${field}: ${msg}`;
    }).join(', ');
  }
  
  // Si es un objeto
  if (typeof detail === 'object') {
    return JSON.stringify(detail);
  }
  
  return 'Error al procesar la solicitud';
};

/**
 * Formatea un error de axios como "HTTP <status> · <url> · <mensaje>".
 * Útil para toasts de diagnóstico que permitan copiar/pegar la causa exacta.
 *
 * @param {any} err - Error de axios
 * @param {string} [fallbackEndpoint] - Nombre del endpoint si no hay config.url
 */
export const describeAxiosError = (err, fallbackEndpoint = '') => {
  const cfg = err?.config || {};
  const resp = err?.response;
  return [
    resp?.status ? `HTTP ${resp.status}` : (err?.code || 'network'),
    cfg?.url || fallbackEndpoint,
    formatErrorMessage(err),
  ].filter(Boolean).join(' · ');
};

/**
 * Ejecuta múltiples requests con Promise.allSettled y devuelve resultados
 * estructurados. Evita que un solo fallo cortocircuite toda la pantalla.
 *
 * @param {Object<string, Promise>} requests - map { nombre_endpoint: promise }
 * @returns {Promise<{values: Object, fallidos: Array<{endpoint, error, detalle}>}>}
 *
 * Uso:
 *   const { values, fallidos } = await settleRequests({
 *     dashboard: api.get('/marketing/dashboard'),
 *     pipeline:  api.get('/leads/pipeline'),
 *   });
 *   if (values.dashboard) setDashboard(values.dashboard.data);
 *   if (fallidos.length) fallidos.forEach(f => toast.error(f.detalle, ...));
 */
export const settleRequests = async (requests) => {
  const entries = Object.entries(requests);
  const results = await Promise.allSettled(entries.map(([, p]) => p));
  const values = {};
  const fallidos = [];
  results.forEach((r, i) => {
    const endpoint = entries[i][0];
    if (r.status === 'fulfilled') {
      values[endpoint] = r.value;
    } else {
      fallidos.push({
        endpoint,
        error: r.reason,
        detalle: `${endpoint}: ${describeAxiosError(r.reason, endpoint)}`,
      });
    }
  });
  return { values, fallidos };
};

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;
